/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { IExercise } from '../domains/Exercise';
import {
  IApplySuggestedTrainingParams,
  IApplyTrainingTemplateParams,
  ICreateTraining,
  IGetTrainings,
  IResponseTrainingSplits,
  ISuggestedDay,
  ISuggestTrainingParams,
  ITraining,
} from '../domains/Training';
import { api } from './api';

class TrainingsService {
  private apiService = api;

  public async getTrainingsByUserIdAndDay({ user_id, day }: IGetTrainings) {
    return this.apiService.get<ITraining[]>(`trainings/${user_id}/${day}`);
  }

  public async getTrainingSplits(user_id: string) {
    return this.apiService.get<IResponseTrainingSplits>(
      `trainings/${user_id}/week`,
    );
  }

  public async createTraining({
    user_id,
    exercise_id,
    split_index,
    sets_min,
    sets_max,
    reps_min,
    reps_max,
    interval_min,
    interval_max,
    additional_information,
  }: ICreateTraining) {
    return this.apiService.post<ITraining>('trainings', {
      user_id,
      exercise_id,
      split_index,
      sets_min,
      sets_max,
      reps_min,
      reps_max,
      interval_min,
      interval_max,
      additional_information,
    });
  }

  public async notifyTrainingUpdated({ user_id }: { user_id: string }) {
    return this.apiService.post<void>('trainings/notify', { user_id });
  }

  // Não existe endpoint que devolva a semana inteira de uma vez -- busca os
  // splits ativos e, em paralelo, os exercícios de cada um.
  public async getFullWeekTrainings(user_id: string) {
    const { data: splitsResponse } = await this.getTrainingSplits(user_id);

    const splits = await Promise.all(
      splitsResponse.groups
        .slice()
        .sort((a, b) => a.index - b.index)
        .map(async group => {
          const { data: trainings } = await this.getTrainingsByUserIdAndDay({
            user_id,
            day: String(group.index),
          });

          return { label: group.label, trainings };
        }),
    );

    return splits.filter(split => split.trainings.length > 0);
  }

  public async finishKiosk({
    student_user_id,
    split_index,
    training_label,
  }: {
    student_user_id: string;
    split_index: number;
    training_label: string;
  }) {
    return this.apiService.post('trainings/kiosk/finish', {
      student_user_id,
      split_index,
      training_label,
    });
  }

  public async removeTrainingSplit({
    user_id,
    split_index,
  }: {
    user_id: string;
    split_index: number;
  }) {
    return this.apiService.delete<void>(
      `trainings/gym/${user_id}/splits/${split_index}`,
    );
  }

  public async suggestTrainingExercises(params: ISuggestTrainingParams) {
    return this.apiService.post<ISuggestedDay[]>('trainings/suggest', params);
  }

  public async applySuggestedTraining(params: IApplySuggestedTrainingParams) {
    return this.apiService.post<ITraining[]>('trainings/suggest/apply', params);
  }

  public async applyTrainingTemplate(params: IApplyTrainingTemplateParams) {
    return this.apiService.post<ITraining[]>(
      'trainings/apply-template',
      params,
    );
  }

  public async cloneTrainings({
    user_id,
    split_index_in,
    split_index_out,
  }: {
    user_id: string;
    split_index_in: number;
    split_index_out: number;
  }) {
    return this.apiService.post<ITraining[]>('trainings/clone', {
      user_id,
      split_index_in,
      split_index_out,
    });
  }
}

export { TrainingsService };
